import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'
import { Alert, AlertDescription } from '../ui/alert'
import { Clock, Timer, Zap, AlertCircle } from 'lucide-react'
import { CheckInInput, AidStation } from '../../types'

const checkInSchema = z.object({
  method: z.union([z.literal('now'), z.literal('clock'), z.literal('elapsed')]),
  clockTime: z.string().optional(),
  elapsedTime: z.string().optional(),
  notes: z.string().optional(),
}).refine((data) => {
  if (data.method === 'clock' && !data.clockTime) {
    return false
  }
  if (data.method === 'elapsed' && !data.elapsedTime) {
    return false
  }
  return true
}, {
  message: "Time input required for selected method"
})

type CheckInFormData = z.infer<typeof checkInSchema>

interface CheckInDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (input: CheckInInput) => Promise<boolean>
  aidStation: AidStation | null
  hasStartTime: boolean
  isLoading?: boolean
  error?: string | null
}

export function CheckInDialog({
  isOpen,
  onClose,
  onSubmit,
  aidStation,
  hasStartTime,
  isLoading = false,
  error = null
}: CheckInDialogProps) {
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<CheckInFormData>({
    resolver: zodResolver(checkInSchema),
    defaultValues: {
      method: hasStartTime ? 'now' : 'elapsed'
    }
  })

  const selectedMethod = watch('method')

  const handleClose = () => {
    reset()
    setSubmitError(null)
    onClose()
  }

  const onFormSubmit = async (data: CheckInFormData) => {
    setSubmitError(null)
    
    const success = await onSubmit({
      method: data.method,
      clockTime: data.clockTime,
      elapsedTime: data.elapsedTime,
      notes: data.notes
    })

    if (success) {
      handleClose()
    } else {
      setSubmitError('Failed to record check-in')
    }
  }

  if (!aidStation) {
    return null
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <span>Check In at {aidStation.name}</span>
          </DialogTitle>
          <DialogDescription>
            Record when the runner passed this aid station ({aidStation.at_distance_km.toFixed(1)} km)
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          {/* Method Selection */}
          <div className="space-y-3">
            <Label className="text-base font-medium">How do you want to record the time?</Label>
            <RadioGroup
              value={selectedMethod}
              onValueChange={(value) => register('method').onChange({ target: { value } })}
              className="space-y-3"
            >
              {hasStartTime && (
                <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 touch-manipulation">
                  <RadioGroupItem value="now" id="now" className="min-w-[20px] min-h-[20px]" />
                  <Label htmlFor="now" className="flex items-center space-x-3 cursor-pointer flex-1 py-2">
                    <Zap className="h-5 w-5 text-blue-500" />
                    <div>
                      <div className="font-medium text-base">Right Now</div>
                      <div className="text-sm text-gray-500">Use current device time</div>
                    </div>
                  </Label>
                </div>
              )}
              
              {hasStartTime && (
                <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 touch-manipulation">
                  <RadioGroupItem value="clock" id="clock" className="min-w-[20px] min-h-[20px]" />
                  <Label htmlFor="clock" className="flex items-center space-x-3 cursor-pointer flex-1 py-2">
                    <Clock className="h-5 w-5 text-green-500" />
                    <div>
                      <div className="font-medium text-base">Clock Time</div>
                      <div className="text-sm text-gray-500">Enter exact time (HH:MM)</div>
                    </div>
                  </Label>
                </div>
              )}
              
              <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-gray-50 touch-manipulation">
                <RadioGroupItem value="elapsed" id="elapsed" className="min-w-[20px] min-h-[20px]" />
                <Label htmlFor="elapsed" className="flex items-center space-x-3 cursor-pointer flex-1 py-2">
                  <Timer className="h-5 w-5 text-purple-500" />
                  <div>
                    <div className="font-medium text-base">Elapsed Time</div>
                    <div className="text-sm text-gray-500">Time since race start (MM:SS or HH:MM:SS)</div>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Time Input Fields */}
          {selectedMethod === 'clock' && (
            <div className="space-y-2">
              <Label htmlFor="clockTime">Clock Time</Label>
              <Input
                id="clockTime"
                {...register('clockTime')}
                placeholder="14:30"
                className="font-mono text-lg py-3 px-4"
                inputMode="numeric"
              />
              <p className="text-sm text-gray-500">
                Enter time in 24-hour format (HH:MM)
              </p>
              {errors.clockTime && (
                <p className="text-sm text-red-600">{errors.clockTime.message}</p>
              )}
            </div>
          )}

          {selectedMethod === 'elapsed' && (
            <div className="space-y-2">
              <Label htmlFor="elapsedTime">Elapsed Time</Label>
              <Input
                id="elapsedTime"
                {...register('elapsedTime')}
                placeholder="125:30 or 2:05:30"
                className="font-mono text-lg py-3 px-4"
                inputMode="numeric"
              />
              <p className="text-sm text-gray-500">
                Enter as MM:SS or HH:MM:SS format
              </p>
              {errors.elapsedTime && (
                <p className="text-sm text-red-600">{errors.elapsedTime.message}</p>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="How is the runner doing? Any observations..."
              rows={3}
            />
          </div>

          {/* Error Display */}
          {(error || submitError) && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {error || submitError}
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting || isLoading}
              className="py-3 px-6 text-base"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="min-w-[120px] py-3 px-6 text-base"
            >
              {isSubmitting || isLoading ? 'Recording...' : 'Record Check-in'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface QuickCheckInButtonsProps {
  onCheckIn: (method: 'now' | 'quick-late' | 'quick-early') => void
  hasStartTime: boolean
  className?: string
}

// Quick action buttons for common scenarios
export function QuickCheckInButtons({ onCheckIn, hasStartTime, className = '' }: QuickCheckInButtonsProps) {
  if (!hasStartTime) {
    return null
  }

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onCheckIn('quick-early')}
        className="text-green-600 border-green-200 hover:bg-green-50 py-2 px-3 text-sm font-medium touch-manipulation"
      >
        5min Early
      </Button>
      <Button
        size="sm"
        onClick={() => onCheckIn('now')}
        className="bg-blue-600 hover:bg-blue-700 py-2 px-3 text-sm font-medium touch-manipulation"
      >
        On Time
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onCheckIn('quick-late')}
        className="text-red-600 border-red-200 hover:bg-red-50 py-2 px-3 text-sm font-medium touch-manipulation"
      >
        5min Late
      </Button>
    </div>
  )
}